/**
 * Hooks for admin user management.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { apiGet } from "../lib/api";

// ----- Child Detail Types -----

export interface ChildDetail {
  child: {
    id: string;
    name: string;
    age: number | null;
    avatarUrl: string | null;
    createdAt: string;
    lastActiveAt: string | null;
  };
  parent: {
    id: string;
    name: string | null;
    email: string;
  };
  stats: {
    totalStars: number;
    totalBadges: number;
    totalPlaytimeMinutes: number;
    challengesCompleted: number;
  };
  disciplines: Array<{
    name: string;
    slug: string;
    character: string;
    currentLevel: number;
    maxLevel: number;
    stars: number;
    lastPlayedAt: string | null;
  }>;
  recentActivity: Array<{
    type: "challenge_completed" | "badge_earned" | "level_completed";
    description: string;
    stars?: number;
    badge?: string;
    timestamp: string;
  }>;
  activityHistory: Array<{
    date: string;
    minutesPlayed: number;
    challengesCompleted: number;
  }>;
}

interface ChildDetailResponse {
  data: ChildDetail;
}

// ----- Types -----

export interface UserListItem {
  id: string;
  email: string | null;
  name: string | null;
  role: string;
  createdAt: string;
  subscription: {
    tier: "free" | "family" | "classroom";
    status: "active" | "canceled" | "past_due" | "trialing" | "incomplete";
    childLimit: number;
  } | null;
  childCount: number;
}

export interface UserListFilters {
  search?: string;
  tier?: "free" | "family" | "classroom";
  status?: "active" | "canceled" | "past_due";
  page?: number;
  limit?: number;
}

interface UserListResponse {
  data: UserListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserDetail {
  profile: {
    id: string;
    email: string | null;
    emailVerified: boolean;
    name: string | null;
    role: string;
    createdAt: string;
    lastLoginAt: string | null;
    provider: string;
    settings: Record<string, unknown>;
  };
  subscription: {
    tier: "free" | "family" | "classroom";
    status: "active" | "canceled" | "past_due" | "trialing" | "incomplete";
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    stripePriceId: string | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    childLimit: number;
    createdAt: string;
  } | null;
  children: Array<{
    id: string;
    name: string;
    avatarId: string | null;
    createdAt: string;
    lastActiveAt: string | null;
    progressSummary: {
      totalStars: number;
      totalBadges: number;
      currentLevels: Record<string, number>;
    };
  }>;
  referral: {
    usedCode: string | null;
    referredBy: { name: string; email: string } | null;
    ownCode: string | null;
    referralCount: number;
  };
}

interface UserDetailResponse {
  data: UserDetail;
}

// ----- useAdminUsers Hook -----

export function useAdminUsers(initialFilters: UserListFilters = {}) {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserListFilters>(initialFilters);

  // Debounce search
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUsers = useCallback(async (currentFilters: UserListFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (currentFilters.search) params.set("search", currentFilters.search);
      if (currentFilters.tier) params.set("tier", currentFilters.tier);
      if (currentFilters.status) params.set("status", currentFilters.status);
      params.set("page", String(currentFilters.page || 1));
      params.set("limit", String(currentFilters.limit || 20));

      const result = await apiGet<UserListResponse>(
        `/api/admin/users?${params.toString()}`
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data) {
        setUsers(result.data.data);
        setPagination(result.data.pagination);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError(err instanceof Error ? err.message : "Failed to load users");
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch and refetch on filter changes
  useEffect(() => {
    fetchUsers(filters);
  }, [fetchUsers, filters]);

  // Debounced search update
  const updateSearch = useCallback((search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search, page: 1 }));
    }, 300);
  }, []);

  // Direct filter updates (no debounce)
  const updateFilters = useCallback((newFilters: Partial<UserListFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  }, []);

  // Page change
  const goToPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    users,
    pagination,
    isLoading,
    error,
    filters,
    updateSearch,
    updateFilters,
    goToPage,
    refetch: () => fetchUsers(filters),
  };
}

// ----- useAdminUserDetail Hook -----

export function useAdminUserDetail(profileId: string | undefined) {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!profileId) {
      setIsLoading(false);
      setError("No profile ID provided");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiGet<UserDetailResponse>(
        `/api/admin/users/${profileId}`
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data) {
        setUser(result.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch user:", err);
      setError(err instanceof Error ? err.message : "Failed to load user");
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    user,
    isLoading,
    error,
    refetch: fetchUser,
  };
}

// ----- useAdminChildDetail Hook -----

export function useAdminChildDetail(childId: string | undefined) {
  const [child, setChild] = useState<ChildDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChild = useCallback(async () => {
    if (!childId) {
      setIsLoading(false);
      setError("No child ID provided");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiGet<ChildDetailResponse>(
        `/api/admin/children/${childId}`
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data) {
        setChild(result.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch child:", err);
      setError(err instanceof Error ? err.message : "Failed to load child");
      setChild(null);
    } finally {
      setIsLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    fetchChild();
  }, [fetchChild]);

  return {
    child,
    isLoading,
    error,
    refetch: fetchChild,
  };
}

// ----- Detailed Progress Types -----

export interface DetailedProgress {
  child: { id: string; name: string };
  discipline: { name: string; slug: string; character: string };
  levels: Array<{
    number: number;
    name: string;
    status: "locked" | "in_progress" | "complete";
    totalChallenges: number;
    completedChallenges: number;
    challenges: Array<{
      id: string;
      name: string;
      order: number;
      type: string;
      status: "locked" | "not_started" | "completed";
      stars: number | null;
      attempts: number;
      completedAt: string | null;
      timeSpentSeconds: number | null;
    }>;
  }>;
  summary: {
    totalChallenges: number;
    completedChallenges: number;
    totalStars: number;
    maxStars: number;
    avgStarsPerChallenge: number;
    avgAttemptsPerChallenge: number;
  };
}

interface DetailedProgressResponse {
  data: DetailedProgress;
}

// ----- useAdminChildProgress Hook -----

export function useAdminChildProgress(
  childId: string | undefined,
  disciplineSlug: string | undefined
) {
  const [progress, setProgress] = useState<DetailedProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProgress = useCallback(async () => {
    if (!childId || !disciplineSlug) {
      setIsLoading(false);
      setError("Child ID and discipline slug are required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiGet<DetailedProgressResponse>(
        `/api/admin/children/${childId}/progress/${disciplineSlug}`
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data) {
        setProgress(result.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch progress:", err);
      setError(err instanceof Error ? err.message : "Failed to load progress");
      setProgress(null);
    } finally {
      setIsLoading(false);
    }
  }, [childId, disciplineSlug]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  return {
    progress,
    isLoading,
    error,
    refetch: fetchProgress,
  };
}
